/**
 * Free-gift / upsell ATC modal (FRI-247, extended in FRI-252).
 *
 * Free-gift mode (FRI-247): rules with `show_modal_on_atc: true` open a
 * modal with N size pickers on ATC; claim batches selections in one
 * POST /cart/add.js. Dismiss falls back to the cart-drawer gift panel.
 *
 * Upsell mode (FRI-252): rules with `offer_type: "upsell"` render a
 * different layout — hero headline + body + display_price + cherry-picked
 * reviews stack. Once-per-session sessionStorage gate so customers
 * aren't pestered on every paddle add. Drawer fallback keeps rendering
 * via cart-drawer.liquid _updateGiftPanel even after modal dismissal.
 *
 * Reads the same #CartGiftRules + #CartGiftVariantMap JSON as the
 * cart-drawer's gift panel, so no duplicate config.
 */
(function () {
  'use strict';
  if (window.__kitGiftModalLoaded) return;
  window.__kitGiftModalLoaded = true;

  var stylesInjected = false;
  var activeModal = null;
  var activeRule = null;
  var claimInFlight = false;

  function injectStyles() {
    if (stylesInjected) return;
    stylesInjected = true;
    var style = document.createElement('style');
    style.textContent = [
      '.kgm-host { position: fixed; inset: 0; z-index: 10000; display: flex; align-items: center; justify-content: center; padding: 16px; opacity: 0; transition: opacity 200ms ease; pointer-events: none; }',
      '.kgm-host[data-visible="true"] { opacity: 1; pointer-events: auto; }',
      '.kgm-backdrop { display: block !important; position: absolute; inset: 0; background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); }',
      '.kgm-card { position: relative; z-index: 1; background: rgb(var(--color-background, 255 255 255)); color: rgb(var(--color-foreground, 0 0 0)); font-family: var(--font-body-family, system-ui, -apple-system, sans-serif); padding: 32px 32px 24px; border-radius: 12px; max-width: 640px; width: calc(100% - 32px); max-height: calc(100vh - 32px); overflow: hidden; box-shadow: 0 16px 56px rgba(0,0,0,0.28); display: flex; flex-direction: column; gap: 14px; transform: scale(0.96); transition: transform 200ms ease; }',
      '.kgm-host[data-visible="true"] .kgm-card { transform: scale(1); }',
      /* Mobile: bottom-sheet — full width, anchored to bottom, slides up. */
      '@media (max-width: 640px) {',
      '  .kgm-host { align-items: flex-end; justify-content: stretch; padding: 0; }',
      '  .kgm-card { width: 100%; max-width: 100%; max-height: 80vh; border-radius: 16px 16px 0 0; padding: 28px 20px 24px; transform: translateY(100%); }',
      '  .kgm-host[data-visible="true"] .kgm-card { transform: translateY(0); }',
      '}',
      '.kgm-close { position: absolute; top: 10px; right: 10px; width: 36px; height: 36px; background: transparent; border: 0; cursor: pointer; font-size: 26px; line-height: 1; color: inherit; opacity: 0.6; padding: 0; }',
      '.kgm-close:hover { opacity: 1; }',
      '.kgm-title { font-size: 20px; font-weight: 600; margin: 0; padding-right: 32px; }',
      '.kgm-description { font-size: 14px; margin: 0; opacity: 0.7; }',
      '.kgm-slots { display: flex; flex-direction: column; gap: 14px; flex: 1 1 auto; min-height: 0; overflow-y: auto; margin: 0 -8px; padding: 2px 8px; }',
      '.kgm-slot { padding: 14px; border: 1px solid rgba(0, 0, 0, 0.1); border-radius: 8px; display: flex; flex-direction: column; gap: 8px; }',
      '.kgm-slot-label { font-size: 11px; font-weight: 600; margin: 0; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.6; }',
      '.kgm-slot-row { display: flex; align-items: center; gap: 12px; }',
      '.kgm-slot-thumb { width: 56px; height: 56px; min-width: 56px; border-radius: 6px; overflow: hidden; background: rgba(0,0,0,0.06); display: flex; align-items: center; justify-content: center; }',
      '.kgm-slot-thumb img { width: 100%; height: 100%; object-fit: cover; }',
      '.kgm-slot-thumb span { font-size: 10px; text-align: center; padding: 4px; }',
      '.kgm-slot-thumb.is-sold-out { opacity: 0.4; }',
      '.kgm-size-select { flex: 1; min-width: 0; font: inherit; font-size: 15px; padding: 12px 14px; border: 1px solid rgba(0, 0, 0, 0.25); border-radius: 6px; background: rgb(var(--color-background, 255 255 255)); color: inherit; cursor: pointer; appearance: auto; max-width: 100%; }',
      '.cart-gift-thumb.is-sold-out { opacity: 0.4; cursor: default; }',
      '.cart-gift-thumb.is-sold-out::after { content: ""; position: absolute; inset: 0; background: linear-gradient(to top right, transparent 47%, currentColor 47%, currentColor 53%, transparent 53%); pointer-events: none; }',
      '.cart-gift-thumb { position: relative; }',
      '.kgm-claim { background: rgb(var(--color-foreground, 0 0 0)); color: rgb(var(--color-background, 255 255 255)); border: 0; padding: 16px 20px; border-radius: 8px; cursor: pointer; font-size: 15px; font-weight: 600; letter-spacing: 0.02em; width: 100%; margin-top: 6px; flex-shrink: 0; }',
      '.kgm-claim:disabled { opacity: 0.55; cursor: not-allowed; }',
      '.kgm-claim.is-sold-out { background: rgb(var(--color-foreground, 0 0 0) / 0.5); }',
      /* Upsell mode (FRI-252) */
      '.kgm-upsell-scroll { display: flex; flex-direction: column; gap: 14px; flex: 1 1 auto; min-height: 0; overflow-y: auto; margin: 0 -8px; padding: 2px 8px; }',
      '.kgm-upsell-headline { font-size: 20px; font-weight: 700; line-height: 1.25; margin: 0; padding-right: 32px; }',
      '.kgm-upsell-body { font-size: 14px; line-height: 1.5; margin: 0; opacity: 0.85; }',
      /* Product section: thumbnail | (title + swatches + price) */
      '.kgm-product-section { display: flex; gap: 14px; align-items: flex-start; }',
      '.kgm-product-thumb { width: 96px; height: 96px; min-width: 96px; border-radius: 8px; overflow: hidden; background: rgba(0,0,0,0.06); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }',
      '.kgm-product-thumb img { width: 100%; height: 100%; object-fit: cover; transition: opacity 0.15s; }',
      '.kgm-product-info { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 8px; }',
      '.kgm-product-title { font-size: 15px; font-weight: 600; line-height: 1.3; margin: 0; }',
      /* Bespoke circular swatches — visual parity with PDP. Reads same
         metafield chain via extended CartGiftVariantMap, but scoped CSS
         (.color-swatch reused from theme.css renders incorrectly in
         the modal context). Selected state matches PDP's outline ring. */
      '.kgm-swatches { display: flex; flex-wrap: wrap; gap: 10px; margin: 0; padding: 0; list-style: none; }',
      '.kgm-swatches li { display: block; line-height: 0; }',
      '.kgm-swatch { display: block; width: 32px; height: 32px; border-radius: 50%; background-color: var(--kgm-swatch-bg, rgba(0,0,0,0.06)); background-size: cover; background-position: center; background-repeat: no-repeat; cursor: pointer; position: relative; box-shadow: inset 0 0 0 1px rgba(0,0,0,0.12); transition: box-shadow 0.15s; }',
      '.kgm-swatch:hover { box-shadow: inset 0 0 0 1px rgba(0,0,0,0.35); }',
      'input.sr-only:checked + .kgm-swatch { box-shadow: 0 0 0 1px rgb(var(--color-background, 255 255 255)), 0 0 0 3px rgb(var(--color-foreground, 0 0 0)); }',
      'input.sr-only:disabled + .kgm-swatch { opacity: 0.4; cursor: not-allowed; }',
      'input.sr-only:disabled + .kgm-swatch::after { content: ""; position: absolute; inset: 0; background: linear-gradient(to top right, transparent 47%, rgb(var(--color-foreground, 0 0 0)) 47%, rgb(var(--color-foreground, 0 0 0)) 53%, transparent 53%); border-radius: inherit; pointer-events: none; }',
      '.kgm-product-price-row { display: flex; align-items: baseline; gap: 8px; margin: 0; }',
      '.kgm-product-price { font-size: 17px; font-weight: 700; }',
      '.kgm-product-price-compare { font-size: 13px; font-weight: 500; opacity: 0.55; text-decoration: line-through; }',
      /* Reviews — uses theme.js slider-element for scroll behavior.
         Cards at 70% width with a 30% peek of the next card. Arrows
         always visible (below center) matching the theme convention. */
      '.kgm-reviews-block { display: flex; flex-direction: column; gap: 8px; }',
      'slider-element.kgm-reviews-slider { display: flex; gap: 10px; overflow-x: auto; scroll-snap-type: x mandatory; scroll-behavior: smooth; -webkit-overflow-scrolling: touch; scrollbar-width: none; padding-bottom: 2px; }',
      'slider-element.kgm-reviews-slider::-webkit-scrollbar { display: none; }',
      '.kgm-review-card { flex: 0 0 70%; scroll-snap-align: start; box-sizing: border-box; padding: 12px 14px; background: rgba(0,0,0,0.04); border-radius: 8px; font-size: 13px; line-height: 1.45; }',
      '.kgm-review-stars { color: #f5a623; font-size: 13px; letter-spacing: 1px; margin: 0 0 4px 0; line-height: 1; }',
      '.kgm-review-text { margin: 0 0 4px 0; }',
      '.kgm-review-author { font-size: 12px; font-weight: 600; opacity: 0.7; margin: 0; }',
      '.kgm-reviews-nav-row { display: flex; justify-content: center; gap: 8px; }',
      '.kgm-reviews-nav { width: 32px; height: 32px; background: rgba(0,0,0,0.06); color: inherit; border: 0; border-radius: 50%; cursor: pointer; font-size: 18px; line-height: 1; padding: 0; display: flex; align-items: center; justify-content: center; transition: background 0.15s, opacity 0.15s; }',
      '.kgm-reviews-nav:hover:not(:disabled) { background: rgba(0,0,0,0.14); }',
      '.kgm-reviews-nav:disabled { opacity: 0.3; cursor: default; }',
      '.kgm-dismiss { background: transparent; color: inherit; border: 0; padding: 6px 12px; margin: 0 auto; font: inherit; font-size: 13px; font-weight: 500; cursor: pointer; opacity: 0.6; text-decoration: underline; flex-shrink: 0; align-self: center; }',
      '.kgm-dismiss:hover { opacity: 1; }',
      '@media (prefers-reduced-motion: reduce) { .kgm-host, .kgm-card { transition: none; } }'
    ].join('\n');
    document.head.appendChild(style);
  }

  // FRI-252: once-per-session dismissal gate for upsell rules. Free-gift
  // rules don't use this (they re-pop on every qualifying ATC).
  function dismissKey(rule) { return 'kgm_dismissed:' + (rule && rule.id ? rule.id : ''); }
  function wasDismissed(rule) {
    try { return window.sessionStorage.getItem(dismissKey(rule)) === '1'; } catch (e) { return false; }
  }
  function markDismissed(rule) {
    try { window.sessionStorage.setItem(dismissKey(rule), '1'); } catch (e) {}
  }

  function readRules() {
    var el = document.getElementById('CartGiftRules');
    if (!el) return [];
    try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
  }

  function readVariantMap() {
    var el = document.getElementById('CartGiftVariantMap');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  }

  function parseIdList(src) {
    return String(src || '')
      .split(',')
      .map(function (s) { return Number(String(s).trim()); })
      .filter(Boolean);
  }

  function resolveOption(opt, variantMap) {
    var vid = opt.variant_id ? Number(opt.variant_id) : null;
    var label = opt.label || '';
    var image = '';
    var swatch_image = '';
    var swatch_color = '';
    var available = true;
    var hidden = false;
    var prod = opt.product ? variantMap[opt.product] : null;
    if (prod) {
      var vname = (opt.variant || '').trim();
      prod.variants.forEach(function (v) {
        if (vid && Number(v.id) === vid) {
          image = v.image; swatch_image = v.swatch_image || ''; swatch_color = v.swatch_color || '';
          available = v.available !== false; hidden = v.hidden === true; if (!label) label = v.title;
        } else if (!vid && vname && v.options.indexOf(vname) !== -1) {
          vid = Number(v.id); image = v.image; swatch_image = v.swatch_image || ''; swatch_color = v.swatch_color || '';
          available = v.available !== false; hidden = v.hidden === true; if (!label) label = v.title;
        } else if (!vid && !vname && !image) {
          vid = Number(v.id); image = v.image; swatch_image = v.swatch_image || ''; swatch_color = v.swatch_color || '';
          available = v.available !== false; hidden = v.hidden === true; if (!label) label = v.title;
        }
      });
      if (!label && prod.title) label = prod.title;
    }
    return vid ? {
      vid: vid, label: label, image: image, available: available, hidden: hidden,
      swatch_image: swatch_image, swatch_color: swatch_color, src: opt
    } : null;
  }

  // FRI-508: is a hidden variant revealed by the session's hidden-variant
  // preview mode? Mirrors session-boot.js: previewHiddenVariants reveals
  // all, previewVariantIds reveals the listed ids.
  function isPreviewRevealed(vid) {
    try {
      if (window.sessionStorage.getItem('previewHiddenVariants') === '1') return true;
      var ids = window.sessionStorage.getItem('previewVariantIds');
      if (ids && ids.split(',').some(function (s) { return Number(s.trim()) === Number(vid); })) return true;
    } catch (e) {}
    return false;
  }

  // FRI-508: options resolving to a variant flagged custom.hide_variant
  // (surfaced as `hidden` in the variant map) are excluded from every
  // picker unless preview mode reveals them. All-hidden = no offer.
  function filterHiddenOptions(resolved) {
    return resolved.filter(function (o) { return !o.hidden || isPreviewRevealed(o.vid); });
  }

  // FRI-508: per-option display overrides. Any of headline / body /
  // display_price / display_price_compare / claim_button_text / reviews set
  // on an options[] entry beats the rule-level value. Price is special-
  // cased: once an option sets display_price it owns the compare price too
  // (absent = no strikethrough), so a cheaper option never inherits another
  // product's compare price. reviewsKey identifies the source so the
  // carousel only rebuilds when the review set actually changes.
  function resolveDisplayFields(rule, opt) {
    var src = (opt && opt.src) || {};
    var hasOwnReviews = Array.isArray(src.reviews) && src.reviews.length > 0;
    var out = {
      headline: src.headline || rule.headline || rule.title || '',
      body: src.body || src.description || rule.body || rule.description || '',
      claimText: src.claim_button_text || rule.claim_button_text || '',
      reviews: hasOwnReviews ? src.reviews : (Array.isArray(rule.reviews) ? rule.reviews : []),
      reviewsKey: hasOwnReviews ? 'opt-' + opt.vid : 'rule'
    };
    if (src.display_price) {
      out.price = String(src.display_price);
      out.compare = src.display_price_compare ? String(src.display_price_compare) : '';
    } else {
      out.price = rule.display_price ? String(rule.display_price) : '';
      out.compare = rule.display_price_compare ? String(rule.display_price_compare) : '';
    }
    return out;
  }

  // Modal triggers on ATC for any rule with show_modal_on_atc: true.
  // Match modes (any one passes):
  //   - trigger_variant_ids (or conditions.items) includes the added vid
  //   - trigger_product_types (comma-sep) includes the added product_type
  // The product_type trigger lets upsell rules like "any paddle → backpack"
  // stay valid as new paddle variants launch, without re-listing IDs.
  // Independent of per_qualifying_unit (slot-count math only).
  function findMatchingModalRule(addedVid, addedProductType) {
    var rules = readRules();
    var addedType = String(addedProductType || '').toLowerCase().trim();
    for (var i = 0; i < rules.length; i++) {
      var r = rules[i];
      if (!r.show_modal_on_atc) continue;

      if (addedType && r.trigger_product_types) {
        var types = String(r.trigger_product_types)
          .split(',')
          .map(function (s) { return s.trim().toLowerCase(); })
          .filter(Boolean);
        if (types.indexOf(addedType) !== -1) return r;
      }

      var triggerSrc = r.trigger_variant_ids != null
        ? r.trigger_variant_ids
        : (r.conditions || {}).items;
      var triggerIds = parseIdList(triggerSrc);
      if (triggerIds.length && triggerIds.indexOf(Number(addedVid)) !== -1) {
        return r;
      }
    }
    return null;
  }

  function computeSlotsRemaining(rule, cart, variantMap) {
    var giftVids = (rule.options || [])
      .map(function (opt) { var r = resolveOption(opt, variantMap); return r ? r.vid : null; })
      .filter(function (v) { return v != null; });
    var giftQty = (cart.items || []).reduce(function (s, i) {
      return giftVids.indexOf(Number(i.variant_id)) !== -1 ? s + i.quantity : s;
    }, 0);

    if (rule.per_qualifying_unit) {
      // Qty-aware: one slot per qualifying trigger item, minus what's
      // already in cart.
      var triggerSrc = rule.trigger_variant_ids != null
        ? rule.trigger_variant_ids
        : (rule.conditions || {}).items;
      var triggerIds = parseIdList(triggerSrc);
      var triggerQty = (cart.items || []).reduce(function (s, i) {
        return triggerIds.indexOf(Number(i.variant_id)) !== -1 ? s + i.quantity : s;
      }, 0);
      return Math.max(0, triggerQty - giftQty);
    }
    // Single-gift rule: show one slot unless any option variant is
    // already in the cart.
    return giftQty > 0 ? 0 : 1;
  }

  function setSlotSelection(slot, opt) {
    slot.setAttribute('data-selected-vid', String(opt.vid));
    slot.setAttribute('data-selected-label', opt.label);
    slot.setAttribute('data-selected-available', opt.available ? '1' : '0');
  }

  // Default labels — override per-rule via rule.claim_button_text
  // (singular) and rule.claim_button_text_multi (with [count] placeholder).
  function defaultClaimLabel(rule, slotCount) {
    var multi = slotCount > 1;
    if (multi && rule && rule.claim_button_text_multi) {
      return String(rule.claim_button_text_multi).replace(/\[count\]/g, String(slotCount));
    }
    if (!multi && rule && rule.claim_button_text) {
      return String(rule.claim_button_text);
    }
    return multi ? 'Claim ' + slotCount + ' Gifts' : 'Claim Gift';
  }

  function refreshClaimButton(host, rule) {
    var btn = host.querySelector('.kgm-claim');
    if (!btn) return;
    var slots = host.querySelectorAll('.kgm-slot');
    var anySoldOut = Array.from(slots).some(function (s) {
      return s.getAttribute('data-selected-available') === '0';
    });
    if (anySoldOut) {
      btn.disabled = true;
      btn.classList.add('is-sold-out');
      btn.textContent = 'Sold out';
    } else {
      btn.disabled = false;
      btn.classList.remove('is-sold-out');
      // FRI-508: upsell modal stamps the selected option's resolved claim
      // text on the host so per-option CTAs survive sold-out round-trips.
      btn.textContent = host.getAttribute('data-kgm-claim') || defaultClaimLabel(rule, slots.length);
    }
  }

  function buildSlot(index, total, resolved, rule) {
    var slot = document.createElement('div');
    slot.className = 'kgm-slot';
    slot.setAttribute('data-slot-index', String(index));

    // Prefer the first available option as the default selection.
    var defaultOpt = resolved.find(function (o) { return o.available; }) || resolved[0];
    setSlotSelection(slot, defaultOpt);

    if (total > 1) {
      var label = document.createElement('p');
      label.className = 'kgm-slot-label';
      label.textContent = 'Gift ' + (index + 1) + ' of ' + total;
      slot.appendChild(label);
    }

    if (rule && rule.picker_style === 'dropdown') {
      var row = document.createElement('div');
      row.className = 'kgm-slot-row';

      var thumb = document.createElement('div');
      thumb.className = 'kgm-slot-thumb';
      var renderThumb = function (opt) {
        thumb.innerHTML = '';
        thumb.classList.toggle('is-sold-out', !opt.available);
        if (opt.image) {
          var img = document.createElement('img');
          img.src = opt.image;
          img.alt = opt.label;
          img.loading = 'lazy';
          img.width = 56;
          img.height = 56;
          thumb.appendChild(img);
        } else {
          var span = document.createElement('span');
          span.textContent = opt.label;
          thumb.appendChild(span);
        }
      };
      renderThumb(defaultOpt);

      var select = document.createElement('select');
      select.className = 'kgm-size-select';
      select.setAttribute('aria-label', 'Choose size for gift ' + (index + 1));
      resolved.forEach(function (opt) {
        var option = document.createElement('option');
        option.value = String(opt.vid);
        option.textContent = opt.label + (opt.available ? '' : ' — Sold out');
        if (!opt.available) option.disabled = true;
        if (opt.vid === defaultOpt.vid) option.selected = true;
        select.appendChild(option);
      });
      select.addEventListener('change', function () {
        var opt = resolved.find(function (o) { return String(o.vid) === select.value; });
        if (!opt) return;
        setSlotSelection(slot, opt);
        renderThumb(opt);
        var host = slot.closest('.kgm-host');
        if (host) refreshClaimButton(host, rule);
      });

      row.appendChild(thumb);
      row.appendChild(select);
      slot.appendChild(row);
      return slot;
    }

    // Default: thumbnails
    var thumbWrap = document.createElement('div');
    thumbWrap.className = 'cart-gift-thumbnails';

    var selectedLine = document.createElement('p');
    selectedLine.className = 'cart-gift-panel__selected';
    selectedLine.textContent = 'Selected: ' + defaultOpt.label + (defaultOpt.available ? '' : ' (Sold out)');

    resolved.forEach(function (opt) {
      var btn = document.createElement('button');
      btn.type = 'button';
      var isDefault = opt.vid === defaultOpt.vid;
      btn.className = 'cart-gift-thumb'
        + (isDefault ? ' is-active' : '')
        + (!opt.available ? ' is-sold-out' : '');
      btn.setAttribute('data-variant-id', String(opt.vid));
      btn.setAttribute('data-label', opt.label);
      if (!opt.available) btn.title = 'Sold out';
      btn.addEventListener('click', function () {
        thumbWrap.querySelectorAll('.cart-gift-thumb').forEach(function (t) {
          t.classList.toggle('is-active', t === btn);
        });
        selectedLine.textContent = 'Selected: ' + opt.label + (opt.available ? '' : ' (Sold out)');
        setSlotSelection(slot, opt);
        var host = slot.closest('.kgm-host');
        if (host) refreshClaimButton(host, rule);
      });
      if (opt.image) {
        var img = document.createElement('img');
        img.src = opt.image;
        img.alt = opt.label;
        img.loading = 'lazy';
        img.width = 56;
        img.height = 56;
        btn.appendChild(img);
      } else {
        var span = document.createElement('span');
        span.style.cssText = 'display:flex;align-items:center;justify-content:center;height:100%;text-align:center;padding:4px;font-size:10px;';
        span.textContent = opt.label;
        btn.appendChild(span);
      }
      thumbWrap.appendChild(btn);
    });

    slot.appendChild(thumbWrap);
    slot.appendChild(selectedLine);
    return slot;
  }

  function escHandler(e) {
    if (e.key === 'Escape' && activeModal) closeModal();
  }

  function closeModal() {
    if (!activeModal) return;
    document.removeEventListener('keydown', escHandler);
    var host = activeModal;
    activeModal = null;
    activeRule = null;
    // Soft close (X / Escape / backdrop / claim success) does NOT mark
    // upsell rules as dismissed. Only an explicit "No thanks" click does
    // (see dismissForSession). The modal re-pops on subsequent paddle
    // ATCs and survives page reloads until "No thanks" is chosen.
    host.setAttribute('data-visible', 'false');
    setTimeout(function () {
      if (host.parentNode) host.parentNode.removeChild(host);
    }, 250);
  }

  // Explicit "No thanks" — suppresses both the modal and the drawer
  // fallback for the rest of the session, then triggers a drawer
  // re-render so the panel disappears immediately.
  function dismissForSession() {
    var rule = activeRule;
    if (rule) markDismissed(rule);
    closeModal();
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function buildModalShell() {
    var host = document.createElement('div');
    host.className = 'kgm-host';
    host.setAttribute('role', 'dialog');
    host.setAttribute('aria-modal', 'true');
    host.setAttribute('aria-labelledby', 'kgm-title');
    host.setAttribute('data-visible', 'false');

    var backdrop = document.createElement('div');
    backdrop.className = 'kgm-backdrop';
    backdrop.addEventListener('click', closeModal);
    host.appendChild(backdrop);

    var card = document.createElement('div');
    card.className = 'kgm-card';

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'kgm-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '×';
    closeBtn.addEventListener('click', closeModal);
    card.appendChild(closeBtn);

    host.appendChild(card);
    return { host: host, card: card };
  }

  function mountAndShow(host, rule) {
    document.body.appendChild(host);
    activeModal = host;
    activeRule = rule;
    requestAnimationFrame(function () { host.setAttribute('data-visible', 'true'); });
    document.addEventListener('keydown', escHandler);
  }

  // Renders a single static rating row, e.g. "★★★★★" for rating=5.
  function buildStars(rating) {
    var n = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    var stars = document.createElement('p');
    stars.className = 'kgm-review-stars';
    stars.setAttribute('aria-label', n + ' out of 5 stars');
    stars.textContent = '★★★★★'.slice(0, n) + '☆☆☆☆☆'.slice(0, 5 - n);
    return stars;
  }

  // Renders the main product thumbnail; reused when a swatch click
  // changes the selected variant (the thumb mirrors the active swatch).
  function paintProductThumb(thumb, opt) {
    thumb.innerHTML = '';
    if (opt.image) {
      var img = document.createElement('img');
      img.src = opt.image;
      img.alt = opt.label || '';
      img.loading = 'lazy';
      img.width = 96; img.height = 96;
      thumb.appendChild(img);
    } else {
      var span = document.createElement('span');
      span.textContent = opt.label || '';
      thumb.appendChild(span);
    }
  }

  // Bespoke swatch picker. Mirrors the PDP swatch metafield chain
  // (snippets/product-variant-picker-swatch.liquid). Source resolution
  // is EXCLUSIVE — exactly one wins, in priority order:
  //   1. swatch_color metafield containing 'gradient' → CSS background-image
  //   2. swatch_color metafield (solid)               → CSS background-color
  //   3. swatch_image metafield (cropped chip)        → CSS background-image
  //   4. variant featured image (final fallback)      → CSS background-image
  // The `data-swatch-source` attribute is set for DevTools debugging so
  // we can tell at a glance which source rendered each swatch.
  function buildSwatches(resolved, defaultOpt, slot, mainThumb, host, rule, onSelect) {
    var ul = document.createElement('ul');
    ul.className = 'kgm-swatches';
    var groupName = 'kgm-variant-' + Math.random().toString(36).slice(2, 9);

    resolved.forEach(function (opt, idx) {
      var li = document.createElement('li');

      var input = document.createElement('input');
      input.type = 'radio';
      input.id = groupName + '-' + idx;
      input.name = groupName;
      input.className = 'sr-only';
      input.value = String(opt.vid);
      if (opt.vid === defaultOpt.vid) input.checked = true;
      if (!opt.available) input.disabled = true;

      var label = document.createElement('label');
      label.setAttribute('for', input.id);
      label.title = opt.label || '';
      label.className = 'kgm-swatch';

      var source = '';
      var styleParts = [];
      if (opt.swatch_color && /gradient/i.test(opt.swatch_color)) {
        source = 'gradient';
        styleParts.push('background-image:' + opt.swatch_color);
      } else if (opt.swatch_color) {
        source = 'color';
        styleParts.push('background-color:' + opt.swatch_color);
      } else if (opt.swatch_image) {
        source = 'metafield-image';
        styleParts.push('background-image:url(' + opt.swatch_image + ')');
      } else if (opt.image) {
        source = 'variant-image';
        styleParts.push('background-image:url(' + opt.image + ')');
      } else {
        source = 'none';
      }
      label.style.cssText = styleParts.join(';');
      label.setAttribute('data-swatch-source', source);

      var sr = document.createElement('span');
      sr.className = 'sr-only';
      sr.textContent = opt.label || '';
      label.appendChild(sr);

      input.addEventListener('change', function () {
        setSlotSelection(slot, opt);
        paintProductThumb(mainThumb, opt);
        if (onSelect) onSelect(opt); // FRI-508: live-swap display fields
        refreshClaimButton(host, rule);
      });

      li.appendChild(input);
      li.appendChild(label);
      ul.appendChild(li);
    });

    return ul;
  }

  // Reviews carousel — reuses theme.js's <slider-element> for scroll +
  // snap, plus plain <button> arrows that dispatch slider:previous /
  // slider:next events (same protocol as the theme's previous-button /
  // next-button web components, but avoids the customized-built-in
  // pattern which Safari doesn't support). Cards at 70% width with a
  // 30% peek so the next card is always discoverable.
  function buildReviewsCarousel(reviews) {
    var block = document.createElement('div');
    block.className = 'kgm-reviews-block';

    var slider = document.createElement('slider-element');
    slider.id = 'kgm-reviews-' + Math.random().toString(36).slice(2, 9);
    slider.className = 'kgm-reviews-slider';
    slider.setAttribute('selector', '.kgm-review-card');

    reviews.forEach(function (rev) {
      var rc = document.createElement('div');
      rc.className = 'kgm-review-card';
      if (rev && rev.rating) rc.appendChild(buildStars(rev.rating));
      if (rev && rev.text) {
        var rt = document.createElement('p');
        rt.className = 'kgm-review-text';
        rt.textContent = String(rev.text);
        rc.appendChild(rt);
      }
      if (rev && rev.author) {
        var ra = document.createElement('p');
        ra.className = 'kgm-review-author';
        ra.textContent = '— ' + String(rev.author);
        rc.appendChild(ra);
      }
      slider.appendChild(rc);
    });

    block.appendChild(slider);

    var navRow = document.createElement('div');
    navRow.className = 'kgm-reviews-nav-row';

    var prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'kgm-reviews-nav';
    prev.setAttribute('aria-label', 'Previous review');
    prev.setAttribute('aria-controls', slider.id);
    prev.textContent = '‹';

    var next = document.createElement('button');
    next.type = 'button';
    next.className = 'kgm-reviews-nav';
    next.setAttribute('aria-label', 'Next review');
    next.setAttribute('aria-controls', slider.id);
    next.textContent = '›';

    prev.addEventListener('click', function () {
      slider.dispatchEvent(new CustomEvent('slider:previous', { bubbles: true, cancelable: true }));
    });
    next.addEventListener('click', function () {
      slider.dispatchEvent(new CustomEvent('slider:next', { bubbles: true, cancelable: true }));
    });

    // Disable arrows at the scroll extremes. slider-element doesn't emit
    // status events for unbound listeners, so we just watch scrollLeft.
    function updateArrows() {
      var atStart = slider.scrollLeft <= 1;
      var atEnd = slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 1;
      prev.disabled = atStart;
      next.disabled = atEnd;
    }
    slider.addEventListener('scroll', updateArrows);
    // Run after the modal becomes visible so layout is settled.
    requestAnimationFrame(function () { requestAnimationFrame(updateArrows); });

    navRow.appendChild(prev);
    navRow.appendChild(next);
    block.appendChild(navRow);

    return block;
  }

  // Layout per FRI-252 spec:
  //   [Headline]
  //   [Body]
  //   [Thumbnail | (Product title from product.title | Swatches | Price+strikethrough)]
  //   [Reviews carousel]
  //   [Add button]
  //   [No thanks]
  function renderUpsellModal(rule, variantMap) {
    injectStyles();
    closeModal();

    var resolved = filterHiddenOptions((rule.options || [])
      .map(function (opt) { return resolveOption(opt, variantMap); })
      .filter(function (o) { return o != null; }));
    if (resolved.length === 0) return;

    var defaultOpt = resolved.find(function (o) { return o.available; }) || resolved[0];
    var defFields = resolveDisplayFields(rule, defaultOpt);

    // Product title — sourced from the first option's product entry in
    // the variant map (Liquid pulls product.title, matching the PDP H1).
    var productTitle = '';
    var firstOpt = (rule.options && rule.options[0]) || null;
    if (firstOpt && firstOpt.product && variantMap[firstOpt.product]) {
      productTitle = variantMap[firstOpt.product].title || '';
    }

    var shell = buildModalShell();
    var host = shell.host;
    var card = shell.card;

    var scroll = document.createElement('div');
    scroll.className = 'kgm-upsell-scroll';

    // 1. Headline — rendered from the default option's resolved fields
    // (FRI-508: per-option override → rule-level fallback); live-swapped
    // by applySelection when the shopper picks a different option. Always
    // created (hidden when empty) so an option-only override has a target.
    var headline = document.createElement('p');
    headline.id = 'kgm-title';
    headline.className = 'kgm-upsell-headline';
    scroll.appendChild(headline);

    // 2. Body
    var body = document.createElement('p');
    body.className = 'kgm-upsell-body';
    scroll.appendChild(body);

    // 3. Product section
    var productSection = document.createElement('div');
    productSection.className = 'kgm-product-section';

    var mainThumb = document.createElement('div');
    mainThumb.className = 'kgm-product-thumb';
    paintProductThumb(mainThumb, defaultOpt);
    productSection.appendChild(mainThumb);

    var info = document.createElement('div');
    info.className = 'kgm-product-info';

    if (productTitle) {
      var pt = document.createElement('p');
      pt.className = 'kgm-product-title';
      pt.textContent = productTitle;
      info.appendChild(pt);
    }

    // Hidden slot tracks the selected vid for handleClaim. Lives inside
    // the card so the standard .kgm-slot[data-selected-vid] query works.
    var slot = document.createElement('div');
    slot.className = 'kgm-slot';
    slot.style.display = 'none';
    setSlotSelection(slot, defaultOpt);
    info.appendChild(slot);

    // Swatches only show for multi-variant upsells (single-variant has
    // nothing to pick — the variant is implicit). applySelection (hoisted,
    // defined below) live-swaps the display fields on every pick.
    if (resolved.length > 1) {
      info.appendChild(buildSwatches(resolved, defaultOpt, slot, mainThumb, host, rule, applySelection));
    }

    var priceRow = document.createElement('p');
    priceRow.className = 'kgm-product-price-row';
    info.appendChild(priceRow);

    productSection.appendChild(info);
    scroll.appendChild(productSection);

    // 4. Reviews carousel — wrapped so applySelection can rebuild it when
    // an option carries its own review set.
    var reviewsWrap = document.createElement('div');
    scroll.appendChild(reviewsWrap);

    card.appendChild(scroll);

    // 5. Add button
    var claimBtn = document.createElement('button');
    claimBtn.type = 'button';
    claimBtn.className = 'kgm-claim';
    claimBtn.addEventListener('click', function () { handleClaim(host, claimBtn); });
    card.appendChild(claimBtn);

    // FRI-508: paint every display field from the given option's resolved
    // fields. Runs once for the default option and again on every swatch
    // change (before refreshClaimButton, which reads data-kgm-claim).
    function applySelection(opt) {
      var f = resolveDisplayFields(rule, opt);
      headline.textContent = f.headline;
      headline.style.display = f.headline ? '' : 'none';
      body.textContent = f.body;
      body.style.display = f.body ? '' : 'none';
      priceRow.textContent = '';
      if (f.price) {
        var price = document.createElement('span');
        price.className = 'kgm-product-price';
        price.textContent = f.price;
        priceRow.appendChild(price);
      }
      if (f.compare) {
        var compare = document.createElement('span');
        compare.className = 'kgm-product-price-compare';
        compare.textContent = f.compare;
        compare.setAttribute('aria-label', 'Regular price ' + f.compare);
        priceRow.appendChild(compare);
      }
      priceRow.style.display = (f.price || f.compare) ? '' : 'none';
      if (reviewsWrap.getAttribute('data-reviews-key') !== f.reviewsKey) {
        reviewsWrap.innerHTML = '';
        if (f.reviews.length > 0) reviewsWrap.appendChild(buildReviewsCarousel(f.reviews));
        reviewsWrap.setAttribute('data-reviews-key', f.reviewsKey);
      }
      host.setAttribute('data-kgm-claim', f.claimText || 'Add to cart');
    }
    applySelection(defaultOpt);

    // 6. No thanks — only path that marks the rule dismissed-for-session
    // (modal + drawer fallback both hide). Soft closes do not.
    var dismissBtn = document.createElement('button');
    dismissBtn.type = 'button';
    dismissBtn.className = 'kgm-dismiss';
    dismissBtn.textContent = rule.dismiss_button_text || 'No thanks';
    dismissBtn.addEventListener('click', dismissForSession);
    card.appendChild(dismissBtn);

    mountAndShow(host, rule);
    refreshClaimButton(host, rule);
  }

  function renderModal(rule, slotCount, variantMap) {
    if (rule && rule.offer_type === 'upsell') {
      return renderUpsellModal(rule, variantMap);
    }
    injectStyles();
    closeModal();

    var resolved = filterHiddenOptions((rule.options || [])
      .map(function (opt) { return resolveOption(opt, variantMap); })
      .filter(function (o) { return o != null; }));
    if (resolved.length === 0) return;

    var shell = buildModalShell();
    var host = shell.host;
    var card = shell.card;

    var title = document.createElement('p');
    title.id = 'kgm-title';
    title.className = 'kgm-title';
    title.textContent = rule.title || 'Claim your free gift';
    card.appendChild(title);

    if (rule.description) {
      var desc = document.createElement('p');
      desc.className = 'kgm-description';
      desc.textContent = rule.description;
      card.appendChild(desc);
    }

    var slotList = document.createElement('div');
    slotList.className = 'kgm-slots';
    for (var i = 0; i < slotCount; i++) {
      slotList.appendChild(buildSlot(i, slotCount, resolved, rule));
    }
    card.appendChild(slotList);

    var claimBtn = document.createElement('button');
    claimBtn.type = 'button';
    claimBtn.className = 'kgm-claim';
    claimBtn.textContent = defaultClaimLabel(rule, slotCount);
    claimBtn.addEventListener('click', function () { handleClaim(host, claimBtn); });
    card.appendChild(claimBtn);

    mountAndShow(host, rule);
    refreshClaimButton(host, rule);
  }

  function handleClaim(host, claimBtn) {
    if (claimInFlight) return;
    claimInFlight = true;
    claimBtn.disabled = true;
    // origText already reflects whatever defaultClaimLabel resolved to
    // for this rule (set by refreshClaimButton at modal open + on every
    // selection change), so we restore to it on error without needing
    // the rule reference here.
    var origText = claimBtn.textContent;
    claimBtn.textContent = 'Adding…';

    var slots = host.querySelectorAll('.kgm-slot');
    var items = [];
    slots.forEach(function (slot) {
      var vid = Number(slot.getAttribute('data-selected-vid'));
      if (vid) items.push({ id: vid, quantity: 1 });
    });

    if (items.length === 0) {
      claimInFlight = false;
      claimBtn.disabled = false;
      claimBtn.textContent = origText;
      return;
    }

    var drawer = document.querySelector('cart-drawer');
    var sectionId = drawer ? drawer.getAttribute('data-section-id') : null;
    var body = { items: items };
    if (sectionId) body.sections = [sectionId];

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart/add.js ' + res.status);
        return res.json();
      })
      .then(function (addResponse) {
        return fetch('/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (addResponse && addResponse.sections) cart.sections = addResponse.sections;
            return cart;
          });
      })
      .then(function (cart) {
        if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'kit-gift-modal',
            cart: cart
          });
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
        }
        closeModal();
        if (drawer && typeof drawer.show === 'function') drawer.show();
      })
      .catch(function () {
        claimBtn.textContent = 'Try again';
        setTimeout(function () {
          claimBtn.textContent = origText;
          claimBtn.disabled = false;
        }, 2000);
      })
      .then(function () { claimInFlight = false; });
  }

  document.addEventListener('ajaxProduct:added', function (e) {
    try {
      var detail = e.detail || {};
      var product = detail.product || {};
      var addedVid = Number(product.variant_id || product.id);
      if (!addedVid) return;

      var rule = findMatchingModalRule(addedVid, product.product_type);
      if (!rule) return;

      // Upsell rules: once-per-session gate so we don't pester on every
      // paddle add. The drawer fallback still renders.
      if (rule.offer_type === 'upsell' && wasDismissed(rule)) return;

      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var variantMap = readVariantMap();
          var slots = computeSlotsRemaining(rule, cart, variantMap);
          if (slots > 0) renderModal(rule, slots, variantMap);
        })
        .catch(function () {});
    } catch (err) {}
  });
})();
